// macadamia-app/src/macadamia-core/components/widgets/composite-text-widget/CompositeTextWidget.tsx
import React, { useCallback, useMemo } from 'react';
import { ICompositeTextWidget, ITextBlock, ISuggestion } from '../types';
import SubBlockEditor from './SubBlockEditor';

interface CompositeTextWidgetProps {
  widget: ICompositeTextWidget;
  onWidgetChange: (newWidget: ICompositeTextWidget) => void;
  mode: 'editor' | 'reader';
  dossierId: string;
  onSuggestionCreated: (newSuggestion: ISuggestion) => void;
  mongoId: string;
  userRole: string | null;
  chapterIndex: number;
  chapterTitle: string;
}

const CompositeTextWidget: React.FC<CompositeTextWidgetProps> = ({
  widget,
  onWidgetChange,
  mode,
  dossierId,
  onSuggestionCreated,
  mongoId,
  userRole,
  chapterIndex,
  chapterTitle
}) => {
  // Mémoïser le handler de changement de bloc de texte
  const handleTextBlockChange = useCallback((index: number, newTextBlock: ITextBlock) => {
    const newBlocks = [...widget.content.blocks];
    newBlocks[index] = newTextBlock;
    onWidgetChange({
      ...widget,
      content: {
        ...widget.content,
        blocks: newBlocks,
      },
    });
  }, [widget, onWidgetChange]);

  // Mémoïser les classes CSS dynamiques
  const containerClasses = useMemo(() => {
    const baseClasses = 'composite-text-widget typo-justify-fr dark:text-mcdm-primary-100';
    const editorClasses = mode === 'editor' ? 'border border-solid border-transparent hover:border-[#f0f0f0]' : '';
    return `${baseClasses} ${editorClasses}`;
  }, [mode]);

  // Mémoïser le rendu des blocs pour éviter les re-renders inutiles
  const renderedBlocks = useMemo(() => {
    return widget.content.blocks.map((block, index) => (
      <SubBlockEditor
        key={`${block._id || index}`} // Utiliser l'ID du bloc si disponible, sinon l'index
        textBlock={block}
        onTextBlockChange={(newTextBlock) => handleTextBlockChange(index, newTextBlock)}
        mode={mode}
        dossierId={dossierId}
        onSuggestionCreated={onSuggestionCreated}
        compositeBlockId={mongoId}
        userRole={userRole}
        chapterIndex={chapterIndex}
        chapterTitle={chapterTitle}
      />
    ));
  }, [widget.content.blocks, handleTextBlockChange, mode, dossierId, onSuggestionCreated, mongoId, userRole, chapterIndex, chapterTitle]);

  return (
    <div
      className={containerClasses}
      style={{ padding: '0px' }}
    >
      {renderedBlocks}
    </div>
  );
};

// Mémoïsation du composant avec comparaison personnalisée
export default React.memo(CompositeTextWidget, (prevProps, nextProps) => {
  // Comparaison des props simples
  if (
    prevProps.mode !== nextProps.mode ||
    prevProps.dossierId !== nextProps.dossierId ||
    prevProps.mongoId !== nextProps.mongoId ||
    prevProps.userRole !== nextProps.userRole ||
    prevProps.chapterIndex !== nextProps.chapterIndex ||
    prevProps.chapterTitle !== nextProps.chapterTitle ||
    prevProps.onWidgetChange !== nextProps.onWidgetChange ||
    prevProps.onSuggestionCreated !== nextProps.onSuggestionCreated
  ) {
    return false;
  }

  // Comparaison approfondie du widget
  if (JSON.stringify(prevProps.widget) !== JSON.stringify(nextProps.widget)) {
    return false;
  }

  return true;
});